import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { toast } from "sonner";
import { useLocale } from "@/hooks/useLocale";
import { tFormat } from "@/lib/i18n/t-format";
import { reportError } from "@/lib/error-reporter";
import type { Translations } from "@/lib/i18n/types";

export interface CreditThresholds {
  /** Can transfer credits (balance >= 100) */
  canTransfer: boolean;
  /** Can use world-building features (balance >= 50) */
  canBuild: boolean;
  /** Can use voice/TTS features */
  canVoice: boolean;
  /** Can use image generation features */
  canImage: boolean;
  /** Can use any credit-consuming feature (balance > 0) */
  canUseCredits: boolean;
}

const THRESHOLD_TRANSFER = 100;
const THRESHOLD_BUILD = 50;
const THRESHOLD_ZERO = 0;

const ALERT_THRESHOLDS = [250, 200, 150, 100, 50, 0] as const;

/**
 * Central hook for credit balance with threshold alerts and capability flags.
 * Shows toast warnings when balance crosses 200, 100, 50, 0.
 */
export function useCreditBalance() {
  const { t } = useLocale();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const prevBalanceRef = useRef<number | null>(null);
  // Prevents state updates after unmount (React 18 strict-mode safe)
  const isMounted = useRef(true);
  // StrictMode-safe: reset on mount so state updates from `load()` are not
  // silently dropped after the simulated first-mount cleanup. See
  // useFeatureFlags for the same pattern.
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const user = await getSessionUser();
      if (!user) { if (isMounted.current) setLoading(false); return; }

      const { data } = await supabase
        .from("user_credits")
        .select("balance")
        .eq("user_id", user.id)
        .maybeSingle();

      const newBalance = data?.balance ?? 0;
      const prev = prevBalanceRef.current;

      // Check if balance crossed a threshold downward
      if (prev !== null && newBalance < prev) {
        for (const threshold of ALERT_THRESHOLDS) {
          if (prev > threshold && newBalance <= threshold) {
            showThresholdAlert(threshold, newBalance, t);
            break; // only show the most relevant alert
          }
        }
      }

      if (isMounted.current) {
        prevBalanceRef.current = newBalance;
        setBalance(newBalance);
      }
    } catch (err) {
      reportError(err, "creditBalance:load");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Poll every 15s so balance stays fresh after AI actions
  useEffect(() => {
    const interval = setInterval(load, 15_000);
    return () => clearInterval(interval);
  }, [load]);

  // Load configurable thresholds from app_settings
  const [thresholdVoice, setThresholdVoice] = useState(150);
  const [thresholdImage, setThresholdImage] = useState(250);

  useEffect(() => {
    supabase
      .from("app_settings")
      .select("key, value")
      .in("key", ["threshold_voice", "threshold_image"])
      .then(({ data }) => {
        // Guard against setting state after unmount (audit #40 cancelled-flag sweep).
        if (!isMounted.current) return;
        for (const row of (data || []) as any[]) {
          if (row.key === "threshold_voice") setThresholdVoice(Number(row.value) || 150);
          if (row.key === "threshold_image") setThresholdImage(Number(row.value) || 250);
        }
      });
  }, []);

  const b = balance ?? 0;

  const thresholds: CreditThresholds = {
    canTransfer: b >= THRESHOLD_TRANSFER,
    canBuild: b >= THRESHOLD_BUILD,
    canVoice: b >= thresholdVoice,
    canImage: b >= thresholdImage,
    canUseCredits: b > THRESHOLD_ZERO,
  };

  return { balance, loading, thresholds, reload: load };
}

function showThresholdAlert(threshold: number, currentBalance: number, t: Translations) {
  const bal = currentBalance.toFixed(0);
  const thr = String(threshold);
  switch (threshold) {
    case 250:
      toast.warning(t.cred_img_disabled, {
        description: tFormat(t.cred_img_disabled_desc, { threshold: thr, balance: bal }),
        duration: 8000,
      });
      break;
    case 200:
      toast.warning(t.cred_running_low, {
        description: tFormat(t.cred_running_low_desc, { balance: bal }),
        duration: 6000,
      });
      break;
    case 150:
      toast.warning(t.cred_voice_disabled, {
        description: tFormat(t.cred_voice_disabled_desc, { threshold: thr, balance: bal }),
        duration: 8000,
      });
      break;
    case 100:
      toast.warning(t.cred_transfer_disabled, {
        description: tFormat(t.cred_transfer_disabled_desc, { threshold: thr, balance: bal }),
        duration: 8000,
      });
      break;
    case 50:
      toast.error(t.cred_build_disabled, {
        description: tFormat(t.cred_build_disabled_desc, { threshold: thr, balance: bal }),
        duration: 10000,
      });
      break;
    case 0:
      toast.error(t.cred_all_disabled, {
        description: t.cred_all_disabled_desc,
        duration: 12000,
      });
      break;
  }
}

/** Threshold constants exported for reuse */
export const CREDIT_THRESHOLDS = {
  TRANSFER: THRESHOLD_TRANSFER,
  BUILD: THRESHOLD_BUILD,
  VOICE: 150,   // default, overridden by app_settings
  IMAGE: 250,   // default, overridden by app_settings
  ZERO: THRESHOLD_ZERO,
} as const;
